import { JobCardVo, Skills } from '@components/job-card/job-card-vo';
import { gql, TypedDocumentNode } from 'apollo-angular';
import { IJob } from 'src/gen';

export const categoryMapping = new Map([
  ['Building and Constructing', 'Realistic'],
  ['Investigating and Testing', 'Investigative'],
  ['Creative Writing', 'Artistic'],
  ['Building Interpersonal Relationships', 'Social'],
  ['Guiding and Directing Others', 'Enterprising'],
  ['Installing Equipment', 'Conventional'],
  ['Repairing and Maintaining Equipment', 'Realistic'],
  ['Getting Information', 'Investigative'],
  ['Thinking Creatively', 'Artistic'],
  ['Providing Consultation and Advice to Others', 'Social'],
  ['Developing Strategies and Objectives', 'Enterprising'],
  ['Creating Systems and Processes', 'Conventional'],
  ['Assembling Equipment and Parts', 'Realistic'],
  ['Analyzing Data or Information', 'Investigative'],
  ['Creative Visualization', 'Artistic'],
  ['Assisting and Caring for Others', 'Social'],
  ['Resolving Conflicts and Negotiating with Others', 'Enterprising'],
  ['Organizing Information', 'Conventional'],
  ['Serving Others', 'Realistic'],
  ['Interpreting the Meaning of Information', 'Investigative'],
  ['Developing Creative Strategies', 'Artistic'],
  ['Giving Time and Attention to Others', 'Social'],
  ['Selling or Influencing Others', 'Enterprising'],
  ['Processing Information', 'Conventional'],
  ['Judging Quality', 'Realistic'],
  ['Monitoring Processes, Materials or Surroundings', 'Investigative'],
  ['Creative Storytelling', 'Artistic'],
  ['Instructing and Teaching Others', 'Social'],
  ['Working and Communicating with the Public', 'Enterprising'],
  ['Categorizing Information', 'Conventional']
]);

export const fetchOccupationListByOnetCodesQuery = (): TypedDocumentNode<unknown, unknown> => gql`
  query ($onetCodes: [ID!]!) {
    occupationsOrCustomByOnetCodes(onetCodes: $onetCodes) {
      __typename
      ... on Occupation {
        onetCode
        educationLevelFrequency {
          educationLevelGroup
          frequency
        }
        typicalEducationLevelGroup
        image {
          urls {
            thumb
          }
        }
        salaryAnalysis {
          minimumAnnualSalary
          medianAnnualSalary
          maximumAnnualSalary
        }
        interestScore {
          interestId
          interestName
          abbreviation
          score
        }
      }
      ... on CustomOccupation {
        onetCode
        educationLevelFrequency {
          educationLevelGroup
          frequency
        }
        typicalEducationLevelGroup
        imageUrl
        salaryAnalysis {
          minimumAnnualSalary
          medianAnnualSalary
          maximumAnnualSalary
        }
        parentOccupation {
          interestScore {
            interestId
            interestName
            abbreviation
            score
          }
        }
      }
    }
  }
`;

const calcSalary: (analysis: any) => string = (analysis: any) => {
  if (analysis.minimumAnnualSalary && analysis.maximumAnnualSalary) {
    const min = annualToHourly(analysis.minimumAnnualSalary);
    const max = annualToHourly(analysis.maximumAnnualSalary);
    return `$${min}-${max}/hour`;
  } else if (analysis.medianAnnualSalary) {
    const median = annualToHourly(analysis.medianAnnualSalary);
    return `$${median}/hour`;
  } else {
    return '';
  }
};

const getKSAs: (ksas: any[], type: string, userKsas?: any[]) => Skills[] = (
  ksas: any[],
  type: string,
  userKsas?: any[]
) => {
  if (!ksas) {
    return [];
  }
  return ksas
    .filter(item => item.ksa.type === type && item.importance >= 2.5)
    .sort((a, b) => b.importance - a.importance)
    .slice(0, 10)
    .map(item => {
      const active = userKsas
        ? userKsas.findIndex(ksa => ksa.displayName === item.ksa.displayName || ksa.displayName === item.ksa.name) > -1
        : false;

      return {
        displayName: item.ksa.displayName,
        active
      };
    });
};

const annualToHourly: (annual: number) => number = (annual: number) => Math.round(annual / 52 / 40);

const getEducationRequirements: (data: any[]) => { label: any; value: any }[] = (data: any[]) => {
  if (!data?.length) {
    return [];
  }

  return data
    .filter(item => item.frequency > 0)
    .map(item => ({
      label: item.educationLevelGroup,
      value: item.frequency
    }));
};

const getTypicalEducationFallback = (frequency: any[], typicalGroup: string | null): string | null => {
  if (frequency?.length) {
    // Full distribution is available — bar chart will render, no text fallback needed
    return null;
  }
  return typicalGroup || null;
};

const generateInterests: (interestCategory: string, interests?: any[]) => any[] = (
  interestCategory: string,
  interests?: any[]
) =>
  [...categoryMapping.entries()]
    .filter(({ 1: v }) => v === interestCategory)
    .map(([k]) => ({
      displayName: k,
      active: interests ? interests.findIndex(interest => interest.reviewText === k) > -1 : false
    }));

export const generateJobDetail = (
  jobDetail: IJob,
  occupation: any,
  userKsas?: { skills?: any[]; interests?: any[] }
): IJob | JobCardVo => {
  if (!occupation) {
    return jobDetail;
  }

  return {
    ...jobDetail,
    education: occupation.typicalEducationLevelGroup,
    salary: calcSalary(occupation.salaryAnalysis),
    workActivities: getKSAs(occupation.occupationKSAs, 'Work Activity'),
    knowledge: getKSAs(occupation.occupationKSAs, 'Knowledge', userKsas?.skills),
    displaySkills: getKSAs(occupation.occupationKSAs, 'Skill', userKsas?.skills),
    abilities: getKSAs(occupation.occupationKSAs, 'Ability', userKsas?.skills),
    educationRequirements: getEducationRequirements(occupation.educationLevelFrequency),
    typicalEducation: getTypicalEducationFallback(occupation.educationLevelFrequency, occupation.typicalEducationLevelGroup),
    videoUrl: occupation.videoUrl,
    imageUrl: occupation.imageUrl || occupation.image?.urls?.regular || occupation.image?.urls?.thumb,
    technologies: occupation.technologies?.slice(0, 10)?.map(item => item.displayName),
    occupationTasks: occupation.occupationTasks,
    occupationKSAs: occupation.occupationKSAs,
    maximumAnnualSalary: occupation.salaryAnalysis?.maximumAnnualSalary || 0,
    minimumAnnualSalary: occupation.salaryAnalysis?.minimumAnnualSalary || 0,
    medianAnnualSalary: occupation.salaryAnalysis?.medianAnnualSalary || 0,
    interests: occupation.interestCategories?.map(category => generateInterests(category, userKsas?.interests)).flat(1)
  };
};
